import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translations from separate files
import frTranslation from './locales/fr/translation.json';
import enTranslation from './locales/en/translation.json';
import arTranslation from './locales/ar/translation.json';

// Resources object
const resources = {
  fr: { translation: frTranslation },
  en: { translation: enTranslation },
  ar: { translation: arTranslation }
};

// Initialize i18n
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'fr', // Default to French if detection fails
    supportedLngs: ['fr', 'en', 'ar'],
    debug: false,
    
    interpolation: {
      escapeValue: false // React already handles escaping
    },
    
    // Language detection configuration
    detection: {
      // Order of detection - navigator first for automatic detection
      order: ['localStorage', 'navigator', 'htmlTag', 'querystring'],
      
      // Look for these query string params
      lookupQuerystring: 'lang',
      
      // Cache user language in localStorage
      caches: ['localStorage'],
      
      // Cookie name if using cookies
      lookupLocalStorage: 'mharher_language',
      
      // Check the user's browser language
      checkWhitelist: true,
      
      // Convert detected language to supported one
      // e.g., 'en-US' -> 'en', 'fr-FR' -> 'fr', 'ar-TN' -> 'ar'
      convertDetectedLanguage: (lng) => {
        if (!lng) return 'fr';
        const base = lng.split('-')[0].toLowerCase();
        if (['fr', 'en', 'ar'].includes(base)) {
          return base;
        }
        return 'fr'; // Default to French for unsupported languages
      }
    },
    
    // React specific settings
    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'span']
    }
  });

// Set document direction and language on initialization
const setDocumentLanguage = (lng) => {
  const language = lng || i18n.language || 'fr';
  document.documentElement.lang = language;
  document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  
  // Set font family based on language
  if (language === 'ar') {
    document.body.style.fontFamily = "'Cairo', 'Plus Jakarta Sans', sans-serif";
  } else {
    document.body.style.fontFamily = "'Plus Jakarta Sans', sans-serif";
  }
};

// Apply on initialization
setDocumentLanguage(i18n.language);

// Listen for language changes
i18n.on('languageChanged', (lng) => {
  setDocumentLanguage(lng);
});

// Helper function to change language
export const changeLanguage = (lng) => {
  i18n.changeLanguage(lng);
  localStorage.setItem('mharher_language', lng);
};

// Get supported languages
export const supportedLanguages = [
  { code: 'fr', name: 'Français', nativeName: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇹🇳', dir: 'rtl' }
];

// Get current language info
export const getCurrentLanguage = () => {
  const currentCode = i18n.language || 'fr';
  return supportedLanguages.find(lang => lang.code === currentCode) || supportedLanguages[0];
};

export default i18n;
